/**
 * ## Merged data based on rule definitions specified
 * in the change object meta data
 * @module src/merge
 */

"use strict";

// not used yet
const clone = require("clone");
let recursionDepth = 0;

const mergeNew = (obj1, obj2, reduced) => {
    ++recursionDepth;
    Object.keys(obj2).forEach((p) => {
        if (typeof obj2[p] === "undefined") {
            obj1[p] = "";
            reduced[p] = "";
        } else if (obj2[p] === "null") {
            obj1[p] = null;
            reduced[p] = null;
        } else if (obj2[p] === "false") {
            obj1[p] = false;
            reduced[p] = false;
        } else if (obj2[p] === "true") {
            obj1[p] = true;
            reduced[p] = true;
        } else if (obj2[p] === null) {
            obj1[p] = null;
            reduced[p] = null;
        } else if (obj2[p] === false) {
            obj1[p] = false;
            reduced[p] = false;
        } else if (
            obj2[p].constructor === Object &&
            p !== "meta" &&
            p !== "$loki" &&
            p !== "_kd"
        ) {
            reduced[p] = obj2[p];
            obj1[p] = obj2[p];
        } else if (obj2[p].constructor === Array) {
            reduced[p] = obj2[p];
            obj1[p] = obj2[p];
        } else if (p !== "$loki" && p !== "meta" && p !== "_kd") {
            reduced[p] = obj2[p];
            obj1[p] = obj2[p];
        }
    });
    --recursionDepth;
    if (recursionDepth === 0) {
        obj1.domain = obj2.domain;
        obj1._id = obj2._id;
        reduced._id = obj1._id;
        reduced.domain = obj1.domain;
        if (obj1.hasOwnProperty("_kd")) {
            reduced._kd = obj1._kd;
        }
        return {
            merged: obj1,
            reduced,
        };
    }
};

/**
 * Integrate object changes into data store by applying a rule policy
 * specific to the proposed changeset.
 *
 * @param {Object} existing - existing data from the data store
 * @param {Object} proposed - proposed change object
 * returns {Object} unchanged or merged data object
 */
const merge = (existing, proposed) => {
    let mergerule = "LWW";
    if (
        proposed.hasOwnProperty("_kd") &&
        proposed._kd.hasOwnProperty("mergerule")
    ) {
        mergerule = proposed._kd.mergerule;
    }
    if (
        proposed.hasOwnProperty("_kd") &&
        !proposed._kd.hasOwnProperty("confirmed") &&
        existing.hasOwnProperty("_kd")
    ) {
        delete existing._kd.confirmed;
    }

    // clone object
    switch (mergerule) {
        case "LWW": {
            // If _kd metadata is not present assume this is an unsynced change
            if (
                !proposed.hasOwnProperty("_kd") ||
                !existing._kd.hasOwnProperty("updated") ||
                (existing.hasOwnProperty("_kd") &&
                    existing._kd.hasOwnProperty("updated") &&
                    proposed._kd.updated >= existing._kd.updated)
            ) {
                delete proposed.meta;
                delete proposed.$loki;
                const merged = mergeNew(clone(existing), proposed, {});
                return merged;
            }
            return existing;
        }
        case "REPLACE": {
            // Fully replace the existing object if it exists
            delete proposed.meta;
            delete proposed.$loki;
            const merged = mergeNew(clone(existing), proposed, {});
            return merged;
        }
        default: {
            return existing;
        }
    }
};

/**
 * Recursive Object merge
 *
 * Will merge object composed of properties, objects, or arrays with
 * nested objects. If putting arbitrary objects in an array, include an
 * "id" property in that object so it can be updated later with just the id.
 * If it's not included that object in the array will be ignored.
 *
 * @param {Object} ex - existing data from the data store
 * @param {Object} pr - proposed change object
 * returns {Object} merged data object
 */
const mergeAll = (ex, pr) => {
    Object.keys(pr).forEach((ky) => {
        let match = false;

        if (typeof pr[ky] === "undefined") {
            ex[ky] = "";
        } else if (pr[ky] == "null") {
            ex[ky] = null;
        } else if (pr[ky] == "false") {
            ex[ky] = false;
        } else if (pr[ky] == "true") {
            ex[ky] = true;
        } else if (pr[ky] == null || pr[ky] == false) {
            ex[ky] = pr[ky];
        } else if (pr[ky].constructor === Array) {
            // is array
            if (!ex.hasOwnProperty(ky)) {
                ex[ky] = [];
            }
            Object.keys(pr[ky]).forEach((pKy) => {
                if (pr[ky][pKy] !== null && pr[ky][pKy].hasOwnProperty("id")) {
                    Object.keys(ex[ky]).forEach((eKy) => {
                        if (ex[ky][eKy].id === pr[ky][pKy].id) {
                            mergeAll(ex[ky][eKy], pr[ky][pKy]);
                            match = true;
                        }
                    });
                    if (!match) {
                        ex[ky].push(pr[ky][pKy]);
                    }
                }
                match = false;
            });
        } else if (pr[ky].constructor === Object && ky !== "meta") {
            // is object
            if (!ex.hasOwnProperty(ky)) {
                ex[ky] = {};
            }
            mergeAll(ex[ky], pr[ky]);
        } else {
            // is single property, _kd or loki objects. Everything ends up here.
            if (ky !== "$loki") {
                ex[ky] = pr[ky];
            }
        }
    });
    return ex;
};

/**
 * Recursive Object merge REPLACE
 *
 * Will merge object composed of properties, objects, or arrays with
 * nested objects. If putting arbitrary objects in an array, include an
 * "id" property in that object so it can be updated later with just the id.
 * If it's not included that object in the array will be ignored.
 *
 * @param {Object} ex - existing data from the data store
 * @param {Object} pr - proposed change object
 * returns {Object} merged data object
 */
const mergeReplace = (ex, pr) => {
    Object.keys(pr).forEach((ky) => {
        if (typeof pr[ky] === "undefined") {
            ex[ky] = "";
        } else if (pr[ky] == "null") {
            ex[ky] = null;
        } else if (pr[ky] == "false") {
            ex[ky] = false;
        } else if (pr[ky] == "true") {
            ex[ky] = true;
        } else if (pr[ky] == null || pr[ky] == false) {
            ex[ky] = pr[ky];
        } else if (
            (pr[ky].constructor === Object || pr[ky].constructor === Array) &&
            ky !== "meta" &&
            ky !== "$loki"
        ) {
            // is object
            ex[ky] = pr[ky];
        } else {
            // is single property, _kd or loki objects. Everything ends up here.
            if (ky !== "$loki") {
                ex[ky] = pr[ky];
            }
        }
    });
    return ex;
};

/**
 * Recursive Object reduce to change set.
 *
 * Will compare two objects and extract changed properties, objects, or
 * arrays with nested objects. If putting arbitrary objects in an array,
 * include an "id" property in that object so it can be updated later with
 * just the id. If it's not included that object in the array will be ignored.
 *
 * @param {Object} ex - existing data from the data store
 * @param {Object} pr - proposed change object
 * @param {Object} ch - change object. Must start as empty {} and changes with
 *    structure will be added.
 * returns {Object} merged data object
 */
const reduce = (ex, pr, ch) => {
    ++recursionDepth;
    Object.keys(pr).forEach((ky) => {
        let match = false;
        if (typeof pr[ky] === "undefined") {
            ex[ky] = "";
        } else if (pr[ky] == "null") {
            ex[ky] = null;
        } else if (pr[ky] == "false") {
            ex[ky] = false;
        } else if (pr[ky] == "true") {
            ex[ky] = true;
        } else if (pr[ky] == null || pr[ky] == false) {
            ex[ky] = pr[ky];
        } else if (pr[ky].constructor === Array) {
            // is array
            if (!ex.hasOwnProperty(ky)) {
                ex[ky] = [];
            }
            Object.keys(pr[ky]).forEach((pKy) => {
                if (pr[ky][pKy] !== null && pr[ky][pKy].hasOwnProperty("id")) {
                    Object.keys(ex[ky]).forEach((eKy) => {
                        if (ex[ky][eKy].id === pr[ky][pKy].id) {
                            if (!ch.hasOwnProperty(ky)) {
                                ch[ky] = [];
                            }
                            if (!ch[ky].hasOwnProperty(pKy)) {
                                ch[ky][pKy] = {
                                    id: pr[ky][pKy].id,
                                };
                            }
                            reduce(ex[ky][eKy], pr[ky][pKy], ch[ky][pKy]);
                            if (Object.keys(ch[ky][pKy]).length <= 1) {
                                ch[ky].splice(pKy, 1);
                            }
                            match = true;
                        }
                    });
                    if (!match) {
                        if (!ch.hasOwnProperty(ky)) {
                            ch[ky] = [];
                        }
                        ch[ky].push(pr[ky][pKy]);
                    }
                }
                match = false;
            });
            if (ch[ky] != undefined) {
                for (let i = 0; i < ch[ky].length; ++i) {
                    if (!ch[ky][i]) {
                        ch[ky].splice(i, 1);
                    }
                }
                if (Object.keys(ch[ky]).length === 0) {
                    delete ch[ky];
                }
            }
        } else if (pr[ky].constructor === Object && ky !== "meta") {
            // is object
            if (!ex.hasOwnProperty(ky)) {
                ex[ky] = {};
            }
            if (!ch.hasOwnProperty(ky)) {
                ch[ky] = {};
            }
            reduce(ex[ky], pr[ky], ch[ky]);
            if (Object.keys(ch[ky]).length === 0) {
                delete ch[ky];
            }
        } else {
            // is single property, _kd or loki objects. Everything ends up here.
            if (ex[ky] !== pr[ky] && ky !== "meta" && ky !== "$loki") {
                ch[ky] = pr[ky];
            }
        }
    });
    --recursionDepth;
    if (recursionDepth === 0) {
        ch.domain = ex.domain;
        ch._id = ex._id;
        ch._kd = pr._kd;
        return ch;
    }
    return ex;
};

const reduceReplace = (ex, pr, ch) => {
    Object.keys(pr).forEach((ky) => {
        if (ex[ky] !== pr[ky] && ky !== "meta" && ky !== "$loki") {
            ch[ky] = pr[ky];
        }
    });
    ch.domain = ex.domain;
    ch._id = ex._id;
    return ch;
};

module.exports = {
    mergeNew,
    merge,
    mergeReplace,
    reduce,
    reduceReplace,
};



/** WEBPACK FOOTER **
 ** ./src/common/merge/index.js
 **/